import { useEffect, useMemo } from "react"
import { useNormalizedRouterSearchParams } from "hooks/useNormalizedRouterSearchParams"
import { defaults, get, isNaN, isNil, mapValues, pick } from "lodash"
import { useVenue } from "providers/venue"
import { useKioskConfig } from "providers/venue/modules/data/kiosk-config/useKioskConfig"
import {
  get as getPersistValue,
  smartSet as smartSetPersistValue,
} from "utils/persist_store"
import {
  MAP_DEFAULT_BEARING_KEY,
  MAP_DEFAULT_PITCH_KEY,
  MAP_DEFAULT_ZOOM_KEY,
} from "constant"

type SafeNumber = (value: any, fallback?: number) => number | undefined
const safeNumber: SafeNumber = (value, fallback) => {
  const parsedValue = Number(value)
  if (isNil(value) || isNaN(parsedValue) || `${value}`.trim() === "")
    return fallback
  return parsedValue
}

export const useMapViewConfig = () => {
  const {
    bearing: bearingQs,
    zoom: zoomQs,
    pitch: pitchQs,
  } = useNormalizedRouterSearchParams<{
    bearing: string
    zoom: string
    pitch: string
  }>()
  const { kioskConfig } = useKioskConfig()
  const { mapConfig } = useVenue()

  const mapViewConfigFromQuery = useMemo(
    () => ({
      bearing: bearingQs,
      zoom: zoomQs,
      pitch: pitchQs,
    }),
    [bearingQs, zoomQs, pitchQs]
  )

  const appMapViewConfig = useMemo(() => {
    return {
      bearing: get(mapConfig, "default_bearing"),
      zoom: get(mapConfig, "default_zoom"),
      pitch: get(mapConfig, "default_pitch"),
    }
  }, [mapConfig])

  const kioskMapViewConfig = useMemo(() => {
    const mapViewConfig = pick(get(kioskConfig, "source"), [
      "bearing",
      "pitch",
      "zoom",
    ])
    // Convert null values to undefined for compatibility with _.defaults method
    const normalizedMapViewConfig = mapValues(mapViewConfig, (v) =>
      isNil(v) ? undefined : v
    )
    return normalizedMapViewConfig
  }, [kioskConfig])

  const defaultMapView = useMemo(() => {
    const storedMapViewConfig = {
      bearing: safeNumber(getPersistValue(MAP_DEFAULT_BEARING_KEY)),
      zoom: safeNumber(getPersistValue(MAP_DEFAULT_ZOOM_KEY)),
      pitch: safeNumber(getPersistValue(MAP_DEFAULT_PITCH_KEY)),
    }

    // Priority: Query String > Kiosk Config > Persisted Local Storage > App Default
    // _.defaults assigns default properties to an object. It fills in undefined values only (not null), from one or more source objects, in order, without overwriting existing defined values.
    const defaultMapViewConfig = defaults(
      {},
      mapViewConfigFromQuery,
      kioskMapViewConfig,
      storedMapViewConfig,
      appMapViewConfig
    )

    return defaultMapViewConfig
  }, [mapViewConfigFromQuery, appMapViewConfig, kioskMapViewConfig])

  useEffect(() => {
    smartSetPersistValue(MAP_DEFAULT_BEARING_KEY, defaultMapView.bearing)
    smartSetPersistValue(MAP_DEFAULT_ZOOM_KEY, defaultMapView.zoom)
    smartSetPersistValue(MAP_DEFAULT_PITCH_KEY, defaultMapView.pitch)
  }, [defaultMapView.bearing, defaultMapView.zoom, defaultMapView.pitch])

  return defaultMapView
}
